import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import {
  ShieldCheck, Certificate, GraduationCap, BuildingOffice, Users, ArrowRight,
  CheckCircle, FileText, QrCode, Handshake, Heart, Leaf, SignIn,
} from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BLUE = "#0B2B66";
const GOLD = "#C9A227";

export default function TrainingAcademy() {
  const nav = useNavigate();
  const [inq, setInq] = useState({
    name: "", organization: "", organization_type: "Hospital",
    phone: "", email: "", region: "", interest_type: "Training Partner", message: "",
  });
  const [busy, setBusy] = useState(false);
  const [ok, setOk] = useState(false);

  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      await axios.post(`${API}/training/public/partner-inquiry`, inq);
      setOk(true);
      setInq({ ...inq, name: "", organization: "", phone: "", email: "", message: "" });
    } catch {
      alert("Submission failed. Please try again.");
    }
    setBusy(false);
  };

  return (
    <div className="min-h-screen bg-[#FAFBFF] text-[#1A1A1A]" data-testid="training-academy">
      {/* NAV */}
      <header className="fixed top-0 inset-x-0 z-40 backdrop-blur-xl bg-white/75 border-b border-[#0B2B66]/10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5 font-heading text-lg" data-testid="ta-brand">
            <div className="w-9 h-9 rounded-lg grid place-items-center text-white"
                 style={{ background: `linear-gradient(135deg, ${BLUE}, ${GOLD})` }}>
              <Certificate size={18} weight="fill" />
            </div>
            <span>Rescigo Academy</span>
          </Link>
          <div className="hidden md:flex items-center gap-7 text-sm">
            <a href="#programs" className="hover:text-[#0B2B66]">Programs</a>
            <a href="#government" className="hover:text-[#0B2B66]">Government</a>
            <a href="#partner" className="hover:text-[#0B2B66]">Partner</a>
            <a href="#contact" className="hover:text-[#0B2B66]">Contact</a>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/training/verify" data-testid="ta-verify-link"
                  className="hidden md:inline-flex h-9 px-3 items-center gap-1.5 text-xs border border-[#0B2B66]/30 rounded-md hover:bg-[#0B2B66]/5">
              <QrCode size={13}/> Verify Certificate
            </Link>
            <Link to="/training/login" data-testid="ta-login"
                  className="h-9 px-4 inline-flex items-center gap-1.5 text-xs font-medium rounded-md text-white"
                  style={{ background: BLUE }}>
              <SignIn size={13} weight="bold"/> User Login Portal
            </Link>
          </div>
        </div>
      </header>

      {/* HERO */}
      <section className="pt-32 pb-20 relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-40 -right-40 w-[600px] h-[600px] rounded-full opacity-[0.06]"
               style={{ background: `radial-gradient(circle, ${BLUE}, transparent)` }} />
          <div className="absolute -bottom-40 -left-40 w-[500px] h-[500px] rounded-full opacity-[0.05]"
               style={{ background: `radial-gradient(circle, ${GOLD}, transparent)` }} />
        </div>
        <div className="max-w-6xl mx-auto px-6 relative">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-[#0B2B66]/15 text-[10px] uppercase tracking-[0.22em] font-semibold text-[#0B2B66] mb-6 shadow-sm">
              <ShieldCheck size={11} weight="fill" color={GOLD}/>
              CPCB-aligned · Court-defensible · Regulatory-grade
            </div>
            <h1 className="font-heading text-5xl md:text-6xl lg:text-7xl tracking-tight font-medium leading-[1.02]">
              India's Advanced<br />
              <span style={{ color: BLUE }}>Biomedical Waste</span><br />
              <span className="italic font-light">Training & Certification Ecosystem</span>
            </h1>
            <p className="text-lg text-[#2D2B26]/80 mt-6 max-w-3xl leading-relaxed">
              Professional training, regulatory compliance certification, and workforce capability
              development for customers, healthcare providers, waste handlers, and public authorities.
            </p>
            <div className="flex flex-wrap gap-3 mt-8">
              <Cta to="/training/login" label="Start Training" Icon={GraduationCap} primary/>
              <Cta to="/training/login" label="Become Certified" Icon={Certificate}/>
              <Cta href="#partner" label="Become a Training Partner" Icon={Handshake}/>
              <Cta href="#government" label="Request Government Demo" Icon={BuildingOffice}/>
            </div>
          </motion.div>

          {/* Trust strip */}
          <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { k: "Training Modules", v: "40+", i: FileText },
              { k: "Active Learners", v: "12K+", i: Users },
              { k: "Certificates Issued", v: "8,420", i: Certificate },
              { k: "Partner Hospitals", v: "260+", i: Handshake },
            ].map((s, i) => (
              <motion.div key={s.k} initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }}
                          viewport={{ once: true }} transition={{ delay: i * 0.08 }}
                          className="bg-white border border-[#0B2B66]/10 rounded-lg p-4">
                <s.i size={16} weight="bold" color={BLUE} />
                <div className="font-heading text-2xl mt-2" style={{ color: BLUE }}>{s.v}</div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#5C5A55] mt-0.5">{s.k}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* PROGRAMS */}
      <section id="programs" className="py-20 bg-white border-y border-[#0B2B66]/10">
        <div className="max-w-6xl mx-auto px-6">
          <SectionHeading eyebrow="Core Programs"
                           title="Professional Certification"
                           subtitle="Structured learning paths for each audience — customer safety, operational staff certification, and government compliance oversight." />
          <div className="grid md:grid-cols-3 gap-6 mt-12">
            {[
              { t: "Biomedical Waste Training Academy", sub: "Customer Safety",
                b: "Color-coded segregation · Spill response · Sharps safety · Storage protocols · Regulatory basics.",
                i: Heart, tag: "customer" },
              { t: "Professional Certification Programs", sub: "Operational Staff",
                b: "Handling · Collection · Transportation · PPE · Chain-of-custody · QR-verified workflows · Advanced processing.",
                i: GraduationCap, tag: "staff" },
              { t: "Government Compliance Training", sub: "Officials / Pollution Boards",
                b: "BMW Rules 2016 · Audit methodology · Enforcement · CPCB reporting · State board integration.",
                i: BuildingOffice, tag: "govt" },
            ].map((p, i) => (
              <motion.div key={p.t} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
                          viewport={{ once: true }} transition={{ delay: i * 0.1 }}
                          className="bg-[#FAFBFF] border border-[#0B2B66]/10 rounded-xl p-6 hover:border-[#0B2B66]/30 transition group">
                <div className="w-12 h-12 rounded-lg grid place-items-center mb-4 text-white"
                     style={{ background: `linear-gradient(135deg, ${BLUE}, ${GOLD})` }}>
                  <p.i size={22} weight="fill"/>
                </div>
                <div className="text-[10px] uppercase tracking-[0.22em] font-semibold" style={{ color: GOLD }}>{p.sub}</div>
                <h3 className="font-heading text-xl mt-1 mb-3 leading-tight">{p.t}</h3>
                <p className="text-sm text-[#2D2B26]/80 leading-relaxed">{p.b}</p>
                <Link to="/training/login" data-testid={`ta-enroll-${p.tag}`}
                      className="inline-flex items-center gap-1 text-xs font-semibold mt-4 group-hover:gap-2 transition-all" style={{ color: BLUE }}>
                  Start this program <ArrowRight size={11} weight="bold"/>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* GOVERNMENT */}
      <section id="government" className="py-20 relative overflow-hidden"
               style={{ background: `linear-gradient(135deg, ${BLUE} 0%, #081C48 100%)` }}>
        <div className="max-w-6xl mx-auto px-6 grid md:grid-cols-2 gap-12 items-center relative">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 border border-white/20 text-[10px] uppercase tracking-[0.22em] font-semibold text-white mb-5">
              <BuildingOffice size={11} weight="fill" color={GOLD}/> National Compliance Platform
            </div>
            <h2 className="font-heading text-4xl md:text-5xl text-white font-medium tracking-tight leading-tight">
              Built for<br/><span style={{ color: GOLD }} className="italic font-light">pollution boards</span><br />and public authorities.
            </h2>
            <p className="text-white/75 mt-5 leading-relaxed">
              Court-defensible compliance certification. Jurisdiction-scoped dashboards. Audit-grade
              evidence. Workforce certification at state and district scale.
            </p>
            <ul className="mt-6 space-y-2">
              {["BMW Rules 2016 — full curriculum", "CPCB audit-ready reporting", "State-board digital verification registry",
                "Legal-grade PDF certificates with QR", "Multi-district jurisdictional scoping"].map((t) => (
                  <li key={t} className="flex items-start gap-2 text-sm text-white/90">
                    <CheckCircle size={14} weight="fill" color={GOLD} className="mt-0.5 flex-shrink-0"/>
                    {t}
                  </li>
              ))}
            </ul>
            <button onClick={() => document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" })}
                    data-testid="ta-gov-demo"
                    className="mt-7 h-11 px-6 inline-flex items-center gap-2 rounded-md font-medium text-sm"
                    style={{ background: GOLD, color: BLUE }}>
              Request Government Demo <ArrowRight size={14} weight="bold"/>
            </button>
          </div>
          <div className="grid grid-cols-2 gap-4">
            {[
              { k: "CPCB", v: "Compliance" },
              { k: "ISO 14001", v: "Environmental" },
              { k: "BMW 2016", v: "Rules aligned" },
              { k: "Court", v: "Defensible" },
            ].map((b) => (
              <div key={b.k} className="aspect-square rounded-xl backdrop-blur-md p-5 border border-white/15 flex flex-col justify-between"
                   style={{ background: "rgba(255,255,255,0.05)" }}>
                <ShieldCheck size={24} weight="fill" color={GOLD}/>
                <div>
                  <div className="font-heading text-2xl text-white">{b.k}</div>
                  <div className="text-[10px] uppercase tracking-[0.22em] text-white/60 mt-1">{b.v}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PARTNER + CONTACT FORM */}
      <section id="partner" className="py-20 bg-[#FAFBFF]">
        <div className="max-w-6xl mx-auto px-6 grid md:grid-cols-5 gap-12">
          <div className="md:col-span-2">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-[#0B2B66]/15 text-[10px] uppercase tracking-[0.22em] font-semibold text-[#0B2B66] mb-4">
              <Handshake size={11} weight="fill" color={GOLD}/> Partner Program
            </div>
            <h2 className="font-heading text-4xl md:text-5xl font-medium tracking-tight leading-tight">
              Become a <span style={{ color: BLUE }} className="italic font-light">Certified Training Partner</span>
            </h2>
            <p className="text-[#2D2B26]/80 mt-5 leading-relaxed">
              Join India's premier biomedical waste training ecosystem. Deliver our curriculum,
              issue co-branded certificates, and build a revenue stream anchored in national
              compliance infrastructure.
            </p>
            <div className="mt-6 space-y-3">
              {[{ t: "Co-branded certification authority", i: Certificate },
                { t: "White-label training platform", i: Leaf },
                { t: "Jurisdiction-exclusive territories", i: BuildingOffice },
                { t: "Revenue share on paid certificates", i: Handshake },
              ].map((it) => (
                <div key={it.t} className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-md grid place-items-center flex-shrink-0"
                       style={{ background: `${BLUE}10`, color: BLUE }}>
                    <it.i size={14} weight="fill"/>
                  </div>
                  <div className="text-sm font-medium text-[#1A1A1A]">{it.t}</div>
                </div>
              ))}
            </div>
          </div>

          <div id="contact" className="md:col-span-3 bg-white border border-[#0B2B66]/15 rounded-2xl p-8 shadow-sm" data-testid="ta-partner-form">
            <h3 className="font-heading text-2xl mb-1">Tell us about your organisation</h3>
            <p className="text-sm text-[#5C5A55] mb-6">We'll respond within 2 working days.</p>
            {ok ? (
              <div className="py-12 text-center" data-testid="ta-partner-success">
                <CheckCircle size={48} weight="fill" color={BLUE} className="mx-auto mb-3"/>
                <div className="font-heading text-xl mb-1">Thank you!</div>
                <div className="text-sm text-[#5C5A55]">Your inquiry has been recorded. Our partnerships team will reach out shortly.</div>
                <button onClick={() => setOk(false)} className="mt-4 text-xs underline" style={{ color: BLUE }}>Submit another</button>
              </div>
            ) : (
              <form onSubmit={submit} className="grid md:grid-cols-2 gap-4">
                <F label="Full Name" v={inq.name} on={(v) => setInq({...inq, name:v})} testid="ta-f-name" required/>
                <F label="Organization" v={inq.organization} on={(v) => setInq({...inq, organization:v})} testid="ta-f-org" required/>
                <Sel label="Organization Type" v={inq.organization_type} on={(v) => setInq({...inq, organization_type:v})} testid="ta-f-orgtype"
                     opts={["Hospital","Clinic","NGO","Government","Training Institute","Corporate","Other"]}/>
                <F label="Phone" v={inq.phone} on={(v) => setInq({...inq, phone:v})} testid="ta-f-phone" required/>
                <F label="Email" type="email" v={inq.email} on={(v) => setInq({...inq, email:v})} testid="ta-f-email" required/>
                <F label="Region / Jurisdiction" v={inq.region} on={(v) => setInq({...inq, region:v})} testid="ta-f-region"/>
                <Sel label="Training Interest" v={inq.interest_type} on={(v) => setInq({...inq, interest_type:v})} testid="ta-f-interest" full
                     opts={["Training Partner","Franchise","Government Accreditation","Corporate Bulk Training","Specialist Instructor","Other"]}/>
                <div className="md:col-span-2">
                  <label className="text-[10px] uppercase tracking-[0.22em] font-medium text-[#5C5A55] block mb-1.5">Message</label>
                  <textarea value={inq.message} onChange={(e) => setInq({...inq, message: e.target.value})}
                            rows={4} data-testid="ta-f-msg"
                            className="w-full px-3 py-2 border border-[#0B2B66]/20 rounded-md text-sm outline-none focus:border-[#0B2B66]"/>
                </div>
                <div className="md:col-span-2">
                  <button type="submit" disabled={busy} data-testid="ta-f-submit"
                          className="w-full h-12 rounded-md text-white font-medium inline-flex items-center justify-center gap-2 disabled:opacity-60"
                          style={{ background: BLUE }}>
                    {busy ? "Submitting…" : "Submit Inquiry"} <ArrowRight size={14} weight="bold"/>
                  </button>
                  <p className="text-[10px] text-[#5C5A55] mt-2 text-center">
                    By submitting, you consent to our partnerships team contacting you about training and certification opportunities.
                  </p>
                </div>
              </form>
            )}
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="py-12 text-white" style={{ background: "#081C48" }}>
        <div className="max-w-6xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-4 text-sm">
          <div className="flex items-center gap-2">
            <Certificate size={18} weight="fill" color={GOLD}/> <span className="font-heading">Rescigo Academy</span>
          </div>
          <div className="text-white/60 text-xs">National Biomedical Waste Training & Certification Ecosystem</div>
          <div className="flex items-center gap-5 text-xs">
            <Link to="/training/verify" className="hover:text-white" data-testid="ta-footer-verify">Verify Certificate</Link>
            <Link to="/terms" className="hover:text-white">Terms</Link>
            <Link to="/privacy" className="hover:text-white">Privacy</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}

const Cta = ({ to, href, label, Icon, primary }) => {
  const cls = `inline-flex items-center gap-2 h-11 px-5 rounded-md text-sm font-medium transition ${primary ? "text-white shadow-lg" : "bg-white border border-[#0B2B66]/20 hover:border-[#0B2B66]/50 text-[#1A1A1A]"}`;
  const style = primary ? { background: BLUE } : {};
  if (to) return <Link to={to} className={cls} style={style}><Icon size={14} weight="fill"/>{label}</Link>;
  return <a href={href} className={cls} style={style}><Icon size={14} weight="fill"/>{label}</a>;
};

const SectionHeading = ({ eyebrow, title, subtitle }) => (
  <div className="text-center max-w-3xl mx-auto">
    <div className="inline-block px-3 py-1 rounded-full bg-[#0B2B66]/5 text-[10px] uppercase tracking-[0.22em] font-semibold" style={{ color: BLUE }}>{eyebrow}</div>
    <h2 className="font-heading text-4xl md:text-5xl mt-4 tracking-tight font-medium">{title}</h2>
    {subtitle && <p className="text-[#2D2B26]/75 mt-3 leading-relaxed">{subtitle}</p>}
  </div>
);

const F = ({ label, v, on, testid, type = "text", required, full }) => (
  <div className={full ? "md:col-span-2" : ""}>
    <label className="text-[10px] uppercase tracking-[0.22em] font-medium text-[#5C5A55] block mb-1.5">{label}</label>
    <input value={v} onChange={(e) => on(e.target.value)} type={type} required={required} data-testid={testid}
           className="w-full h-11 px-3 border border-[#0B2B66]/20 rounded-md text-sm outline-none focus:border-[#0B2B66]"/>
  </div>
);

const Sel = ({ label, v, on, testid, opts, full }) => (
  <div className={full ? "md:col-span-2" : ""}>
    <label className="text-[10px] uppercase tracking-[0.22em] font-medium text-[#5C5A55] block mb-1.5">{label}</label>
    <select value={v} onChange={(e) => on(e.target.value)} data-testid={testid}
            className="w-full h-11 px-3 border border-[#0B2B66]/20 rounded-md text-sm outline-none focus:border-[#0B2B66] bg-white">
      {opts.map((o) => <option key={o}>{o}</option>)}
    </select>
  </div>
);
